import { useCallback, useEffect, useRef, useState } from "react";

import { trackChatComposerEvent } from "@common/analytics";
import { useAnalytics } from "../context/AnalyticsContext";
import { useAuthHeader } from "../context/AuthContext";
import { attachConversationToProject, getConversationProject } from "../utils/api";

export type AttachedProject = { id: string; name: string } | null;

interface UseConversationProjectResult {
  project: AttachedProject;
  attach: (projectId: string) => Promise<void>;
}

export function useConversationProject(conversationId: string | undefined): UseConversationProjectResult {
  const authHeader = useAuthHeader();
  const { analytics } = useAnalytics();
  const [project, setProject] = useState<AttachedProject>(null);

  const refreshSeqRef = useRef(0);

  const activeConversationIdRef = useRef(conversationId);
  activeConversationIdRef.current = conversationId;

  useEffect(() => {
    setProject(null);
  }, [conversationId]);

  const refresh = useCallback(async () => {
    const refreshSeq = ++refreshSeqRef.current;
    if (!conversationId || !authHeader) {
      setProject(null);
      return;
    }
    try {
      const data = await getConversationProject(authHeader, conversationId);
      if (refreshSeqRef.current === refreshSeq) {
        setProject(data);
      }
    } catch {
      if (refreshSeqRef.current === refreshSeq) {
        setProject(null);
      }
    }
  }, [conversationId, authHeader]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const hasAuth = !!authHeader;
  useEffect(() => {
    refreshRef.current();
  }, [conversationId, hasAuth]);

  const attach = useCallback(async (projectId: string) => {
    if (!conversationId || !authHeader) return;
    const result = await attachConversationToProject(authHeader, projectId, conversationId);
    trackChatComposerEvent(
      { sink: analytics, surface: "word-addin", conversationId },
      { name: "chat_composer_workspace_attached", workspaceId: projectId },
    );
    if (activeConversationIdRef.current === conversationId) {
      setProject(result);
    }
  }, [conversationId, authHeader, analytics]);

  return { project, attach };
}
